import React from 'react';
import shortid from 'shortid';
import delay from 'javascripts/delay';
import { Button, Modal, Tooltip, OverlayTrigger } from '../../legacy-bootstrap';

interface ConfirmProps {
  body: React.ReactNode;
  buttonText?: React.ReactNode;
  cancelText?: React.ReactNode;
  className?: string;
  confirmBSStyle?: string;
  confirmText?: React.ReactNode;
  dialogClassName?: string;
  onConfirm: (value: boolean) => void;
  setHidePromptChecked?: () => void;
  onClose?: () => void;
  onButtonClick?: () => void;
  showCancelButton?: boolean;
  title: React.ReactNode;
  visible?: boolean;
  tooltip?: string;
  showCheckbox?: boolean;
  showOpenButton?: boolean;
  iconUrl?: string;
  confirmButtonStyle?: string;
  style?: React.CSSProperties;
  focusOnOpen?: boolean;
  children?: React.ReactNode;
}

interface ConfirmState {
  isOpened: boolean;
  hidePromptChecked: boolean;
  enforceFocus: boolean;
}

class Confirm extends React.Component<ConfirmProps, ConfirmState> {
  constructor(props: ConfirmProps) {
    super(props);
    this.state = {
      isOpened: false,
      hidePromptChecked: false,
      enforceFocus: false,
    };
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onConfirm = this.onConfirm.bind(this);
    this.setHidePromptChecked = this.setHidePromptChecked.bind(this);
  }

  onButtonClick() {
    // Since the modal is inside the button click events will propagate up.
    if (!this.state.isOpened) {
      this.setState({
        isOpened: true,
      });
    }
    if (typeof this.props.onButtonClick === 'function') {
      this.props.onButtonClick();
    }
  }

  onClose(event) {
    if (event) {
      event.stopPropagation();
    }
    this.setState({
      isOpened: false,
    });

    if (typeof this.props.onClose === 'function') {
      this.props.onClose();
    }
  }

  onConfirm(event) {
    event.stopPropagation();
    this.setState({
      isOpened: false,
    });
    this.props.onConfirm(this.state.hidePromptChecked);
  }

  setHidePromptChecked(event) {
    event.stopPropagation();
    this.setState({
      hidePromptChecked: event.currentTarget.checked,
    });
  }

  componentDidMount() {
    // There's a race condition in the "My work page", where enforce focus
    // encounters gets into an infinite recursion when opening the confirm
    // modal for deleting a note/whiteboard. It happens because the focus is
    // still on the "Delete this note" element for a moment.
    // added a focusOnOpen option to the prop to avoid this race condition.
    // this is only used in the adming cs scheduler as a work around.
    if (!this.state.enforceFocus && this.props.focusOnOpen) {
      this.setState({ enforceFocus: true });
    }
  }

  render() {
    const cancelButton = this.props.showCancelButton ? (
      <Button bsStyle='default' aria-label='cancel' onClick={this.onClose}>
        {this.props.cancelText}
      </Button>
    ) : null;
    const closeButton = this.props.showCancelButton ? (
      <button className='close' aria-label='close modal' onClick={this.onClose}>
        ×
      </button>
    ) : null;
    const icon = this.props.iconUrl ? <img src={this.props.iconUrl} /> : null;
    const confirmButtonStyle = this.props.confirmButtonStyle
      ? `btn-${this.props.confirmButtonStyle}`
      : 'btn-primary';

    const body =
      typeof this.props.body === 'string' ? (
        <div>
          <p>{this.props.body}</p>
        </div>
      ) : (
        this.props.body
      );

    const checkbox = this.props.showCheckbox ? (
      <p style={{ marginLeft: '40px' }}>
        <input
          id='hidePromptCheckbox'
          aria-label='do not show prompt again'
          type='checkbox'
          checked={this.state.hidePromptChecked}
          onChange={e => this.setHidePromptChecked(e)}
        />
        <label
          htmlFor='hidePromptCheckbox'
          style={{ display: 'inline', marginLeft: '1ch', verticalAlign: 'top' }}
        >
          Do not show again
        </label>
      </p>
    ) : null;

    const modal = (
      <Modal
        show={this.state.isOpened || this.props.visible}
        onHide={this.onClose}
        id='confirmation_dialog'
        enforceFocus={this.state.enforceFocus}
      >
        <Modal.Header>
          {closeButton}
          <Modal.Title>{this.props.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {icon}
          {body}
          {checkbox}
        </Modal.Body>
        <Modal.Footer>
          {cancelButton}
          &nbsp;
          <Button
            className={confirmButtonStyle}
            onClick={this.onConfirm}
            style={{ width: 'auto' }}
            aria-label='confirm'
          >
            {this.props.confirmText}
          </Button>
        </Modal.Footer>
      </Modal>
    );
    let content;
    if (!this.props.showOpenButton) {
      content = modal;
    } else if (this.props.children) {
      const btn = React.Children.only(
        this.props.children
      ) as React.ReactElement;
      content = React.cloneElement(
        btn,
        {
          onClick: this.onButtonClick,
          style: this.props.style,
        },
        btn.props.children,
        modal
      );
    } else {
      content = (
        <Button onClick={this.onButtonClick} style={this.props.style}>
          {this.props.buttonText}
          {modal}
        </Button>
      );
    }

    if (this.props.tooltip) {
      const tooltip = (
        <Tooltip id={shortid.generate()}>{this.props.tooltip}</Tooltip>
      );
      content = (
        <OverlayTrigger
          placement='top'
          trigger={['hover']}
          overlay={tooltip}
          rootClose
          delayShow={delay.show}
          delayHide={delay.hide}
        >
          {content}
        </OverlayTrigger>
      );
    }

    return content;
  }
}

// @ts-expect-error defaultProps is not defined
Confirm.defaultProps = {
  cancelText: 'Cancel',
  confirmText: 'Confirm',
  confirmBSStyle: 'danger',
  showCancelButton: true,
  showCheckbox: false,
  showOpenButton: true,
  focusOnOpen: true,
};

export default Confirm;
